import { useState } from 'react';
import { Link } from 'react-router-dom';
import { comics, featuredComicIds, hotComicIds, newUpdateIds, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

function ComicCard({ comic }: { comic: Comic }) {
  const [imgError, setImgError] = useState(false);
  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group block"
    >
      <div className="relative overflow-hidden rounded-lg bg-background-100">
        <div className="aspect-[3/4] w-full overflow-hidden">
          {imgError ? (
            <div className="w-full h-full flex items-center justify-center bg-background-200 text-foreground-400">
              <div className="w-12 h-12 flex items-center justify-center">
                <i className="ri-image-line text-3xl"></i>
              </div>
            </div>
          ) : (
            <img
              src={comic.coverImage}
              alt={`${comic.title} - 韩国漫画`}
              title={`${comic.title} - 免费韩国漫画在线阅读`}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              onError={() => setImgError(true)}
            />
          )}
        </div>
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${
            comic.status === '连载中'
              ? 'bg-primary-500 text-background-50'
              : 'bg-secondary-500 text-background-50'
          }`}>
            {comic.status}
          </span>
        </div>
        <div className="absolute top-2 right-2">
          <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-background-950/70 text-background-50">
            {comic.rating}分
          </span>
        </div>
      </div>
      <div className="mt-3 px-0.5">
        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
          {comic.title}
        </h3>
        <p className="mt-1 text-xs text-foreground-500">{comic.author}</p>
        <div className="mt-1.5 flex items-center gap-3">
          <span className="text-xs text-primary-600 font-medium">
            <span className="inline-flex items-center gap-0.5">
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-eye-line text-xs"></i>
              </div>
              {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views}
            </span>
          </span>
          <span className="text-xs text-foreground-400">{comic.category}</span>
        </div>
      </div>
    </Link>
  );
}

function FeaturedCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const featuredComics = featuredComicIds.map((id) => comics.find((c) => c.id === id)).filter(Boolean) as Comic[];

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % featuredComics.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + featuredComics.length) % featuredComics.length);
  };

  const currentComic = featuredComics[currentIndex];
  if (!currentComic) return null;

  return (
    <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={currentComic.coverImage}
          alt={currentComic.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
      </div>

      <div className="relative z-10 h-full flex items-center">
        <div className="px-4 md:px-6 w-full">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-2xl">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50 mb-4">
                热门推荐
              </span>
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4 leading-tight">
                {currentComic.title}
              </h1>
              <p className="text-sm md:text-base text-background-200/90 leading-relaxed mb-6 line-clamp-3">
                {currentComic.description}
              </p>
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="text-sm text-background-300">{currentComic.author}</span>
                <span className="text-background-400">·</span>
                <span className="text-sm text-background-300">{currentComic.category}</span>
                <span className="text-background-400">·</span>
                <span className="text-sm text-accent-400 font-semibold">{currentComic.rating}分</span>
                <span className="text-background-400">·</span>
                <span className="text-sm text-background-300">{currentComic.status}</span>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to={`/comic/${currentComic.id}`}
                  className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  开始阅读
                </Link>
                <Link
                  to="/category/all"
                  className="px-6 py-2.5 border border-background-300/50 text-background-50 rounded-full text-sm font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap"
                >
                  浏览全部
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={prevSlide}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/40 text-background-50 transition-colors cursor-pointer"
        aria-label="上一个"
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-left-s-line text-xl"></i>
        </div>
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/40 text-background-50 transition-colors cursor-pointer"
        aria-label="下一个"
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-right-s-line text-xl"></i>
        </div>
      </button>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {featuredComics.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
              index === currentIndex ? 'bg-primary-500 w-6' : 'bg-background-50/50 hover:bg-background-50/80'
            }`}
            aria-label={`第${index + 1}张`}
          />
        ))}
      </div>
    </section>
  );
}

export default function HomePage() {
  const featuredComics = featuredComicIds.map((id) => comics.find((c) => c.id === id)).filter(Boolean) as Comic[];
  const hotComics = hotComicIds.map((id) => comics.find((c) => c.id === id)).filter(Boolean) as Comic[];
  const newUpdates = newUpdateIds.map((id) => comics.find((c) => c.id === id)).filter(Boolean) as Comic[];

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': '韩漫之家 - 免费韩国漫画在线阅读',
    'description': '韩漫之家为您呈现免费韩漫和韩国漫画下拉式在线阅读，拥有高清漫画大全资源。',
    'url': 'https://www.manhuazhijia.com/',
    'isPartOf': {
      '@type': 'WebSite',
      'name': '韩漫之家',
      'url': 'https://www.manhuazhijia.com/',
    },
    'breadcrumb': {
      '@type': 'BreadcrumbList',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'name': '首页',
          'item': 'https://www.manhuazhijia.com/',
        },
      ],
    },
  };

  return (
    <>
      <head>
        <script type="application/ld+json">
          {JSON.stringify(homeSchema)}
        </script>
      </head>

      <main>
        <FeaturedCarousel />

        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">热门推荐</h2>
                <p className="mt-1 text-sm text-foreground-500">精选优质韩国漫画，人气与口碑的保证</p>
              </div>
              <Link
                to="/category/all"
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
              >
                查看全部
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {featuredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">漫画分类</h2>
              <p className="mt-1 text-sm text-foreground-500">探索不同类型的韩国漫画，找到你的最爱</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-2 p-4 rounded-lg bg-background-50 hover:bg-primary-50 transition-colors"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all">
                    <i className={`text-xl ${
                      cat.slug === 'all' ? 'ri-apps-2-line' :
                      cat.slug === 'romance' ? 'ri-heart-line' :
                      cat.slug === 'action' ? 'ri-sword-line' :
                      cat.slug === 'fantasy' ? 'ri-magic-line' :
                      cat.slug === 'mystery' ? 'ri-search-eye-line' :
                      cat.slug === 'comedy' ? 'ri-emotion-laugh-line' :
                      cat.slug === 'drama' ? 'ri-film-line' :
                      cat.slug === 'horror' ? 'ri-ghost-line' :
                      'ri-heart-2-line'
                    }`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-700 whitespace-nowrap">{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">人气排行榜</h2>
                <p className="mt-1 text-sm text-foreground-500">根据阅读量和评分综合排序，看看大家都在看什么</p>
              </div>
              <Link
                to="/category/all"
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
              >
                更多排行
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {hotComics.slice(0, 6).map((comic, index) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group flex gap-4 p-4 rounded-lg bg-background-50 hover:bg-background-100 transition-colors"
                >
                  <div className="relative flex-shrink-0">
                    <div className="w-20 h-28 md:w-24 md:h-32 rounded-md overflow-hidden bg-background-200">
                      <img
                        src={comic.coverImage}
                        alt={`${comic.title} - 韩国漫画`}
                        title={`${comic.title} - 免费韩国漫画在线阅读`}
                        className="w-full h-full object-cover object-top"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                    <div className="absolute -top-2 -left-2 w-7 h-7 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-xs font-bold">
                      {index + 1}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {comic.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-500">{comic.author}</p>
                    <p className="mt-1.5 text-xs text-foreground-400 line-clamp-2">{comic.description}</p>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs text-accent-600 font-semibold">{comic.rating}分</span>
                      <span className="text-xs text-foreground-400">{comic.category}</span>
                      <span className={`text-xs font-medium ${
                        comic.status === '连载中' ? 'text-primary-600' : 'text-secondary-600'
                      }`}>{comic.status}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">最新更新</h2>
                <p className="mt-1 text-sm text-foreground-500">刚刚更新的漫画章节，追更不迷路</p>
              </div>
              <Link
                to="/category/all"
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
              >
                全部更新
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {newUpdates.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">关于韩漫之家</h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              <strong>韩漫之家</strong>是一个专注于<strong>免费韩漫</strong>和<strong>韩国漫画下拉式在线阅读</strong>的平台，拥有丰富的<strong>高清漫画大全</strong>资源。我们致力于为中文读者提供最优质的韩国漫画阅读体验，涵盖恋爱、动作、奇幻、悬疑、喜剧等多种类型，让每一位漫迷都能在这里找到心仪的作品。无论你是喜欢热血战斗、甜蜜爱情还是悬疑推理，韩漫之家都能满足你对多样化故事的需求。
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 mt-6 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              了解更多
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}